"use client"

import type { CompanyListContentProps } from "@/types/types"
import { Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

import "swiper/css"
import "swiper/css/pagination"
import "./companyList.css"

import { useCompanyList } from "../../../../../../../../hook/useCompanyList"
import { CompanyListItem } from "./CompanyListItem"

export const CompanyListContent = ({
  id,
  title,
  companies = [],
  gap,
  maxWidth,
  titleFontSize,
  titleFontWeight,
  titleLineHeight,
  className = "",
}: CompanyListContentProps) => {
  const { validCompanies, hasValidCompanies, swiperConfig } = useCompanyList({
    companies,
    componentId: id,
  })

  if (!hasValidCompanies) {
    return null
  }

  const desktopGapStyle = gap
    ? {
      columnGap: `${gap}px`,
      rowGap: `${gap}px`,
    }
    : undefined

  const mobileSpaceBetween = gap ?? 0

  // Title styles
  const titleStyle: Record<string, string | number> = {}
  const titleClassName = "text-center sm:pb-12 pb-[2.875rem] pt-4 sm:pt-0 text-[#333333]"

  const titleStyleConfig = [
    { key: "fontSize", value: titleFontSize },
    { key: "fontWeight", value: titleFontWeight },
    { key: "lineHeight", value: titleLineHeight },
  ]

  titleStyleConfig.forEach(config => {
    if (config.value !== undefined) {
      switch (config.key) {
        case "fontSize":
          titleStyle.fontSize = `${config.value}px`
          break
        case "fontWeight":
          titleStyle.fontWeight = config.value
          break
        case "lineHeight":
          titleStyle.lineHeight = `${config.value}px`
          break
      }
    }
  })

  // Build className for title - only add default classes if custom values are not provided
  const titleClassNames = [
    titleClassName,
    titleFontSize === undefined ? "text-2xl" : "",
    titleFontWeight === undefined ? "font-extrabold" : "",
    titleLineHeight === undefined ? "sm:leading-[2.3125rem] leading-[1.625rem]" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={`w-full ${className}`} id={id}>
      <div className="mx-auto max-w-full sm:max-w-[1304px] pt-0 sm:px-2 pb-0 sm:pb-[5.8125rem] px-4">
        {title && (
          <h2 className={titleClassNames} style={Object.keys(titleStyle).length > 0 ? titleStyle : undefined}>
            {title}
          </h2>
        )}

        {/* Desktop Grid Layout - 5 items per row, centered */}
        <div
          style={{
            ...(maxWidth !== undefined && { maxWidth: `${maxWidth}px` }),
          }}
          className={maxWidth !== undefined ? "mx-auto" : "sm:mx-[193px]"}
        >
          <div
            className="hidden sm:grid sm:grid-cols-5 sm:justify-center sm:justify-items-center sm:items-start"
            style={desktopGapStyle}
          >
            {validCompanies.map((company, index) => {
              const totalItems = validCompanies.length
              const itemsPerRow = 5
              const fullRows = Math.floor(totalItems / itemsPerRow)
              const itemsInLastRow = totalItems % itemsPerRow

              // Xác định item có thuộc hàng cuối cùng không
              const isLastRow = itemsInLastRow > 0 && index >= fullRows * itemsPerRow

              let itemStyle: React.CSSProperties | undefined = undefined

              if (isLastRow && itemsInLastRow < itemsPerRow) {
                // Tính vị trí trong hàng cuối (0-based trong hàng cuối)
                const positionInLastRow = index - fullRows * itemsPerRow

                // Tính offset để căn giữa hàng cuối
                // Ví dụ: nếu có 3 items trong hàng cuối, offset = (5-3)/2 = 1
                // Items sẽ bắt đầu từ col 2, 3, 4
                const offset = Math.floor((itemsPerRow - itemsInLastRow) / 2)

                // Tính col-start (1-based trong CSS Grid)
                const colStart = offset + positionInLastRow + 1
                itemStyle = { gridColumnStart: colStart }
              }

              return <CompanyListItem key={index} company={company} style={itemStyle} />
            })}
          </div>
        </div>

        {/* Mobile Carousel Layout */}
        <div className="block sm:hidden">
          <Swiper
            modules={[Pagination]}
            loop={swiperConfig.loop}
            pagination={swiperConfig.pagination}
            slidesPerView={2}
            spaceBetween={mobileSpaceBetween}
            className="company-list-swiper w-full"
          >
            {validCompanies.map((company, index) => (
              <SwiperSlide key={index}>
                <CompanyListItem company={company} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  )
}
